import { useState } from 'react'

import {
  Box,
  Button,
  Variants as ButtonVariants,
  Section,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type { AuthInfo } from 'modules/personal_settings/components/Settings/types'
import Cognito from 'modules/personal_settings/store/models/Cognito'

import MFASetupModal from './modal'

const MFASettings = () => {
  const [openModal, setOpenModal] = useState(false)

  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null)

  const fetchAuthInformation = async () => {
    const api = ApiClient()
    const result = await api.get('/cognito/users/current')
    setAuthInfo(result)
    setMfaEnabled(!!result.mfa_enabled)
  }

  const { loading: loadingAuthInfo } = useAsyncData(fetchAuthInformation, [])
  const canRenderMFA = !!authInfo && !loadingAuthInfo
  const mfaAvailable = !authInfo?.oauth_login
  const cognitoAPI = new Cognito({})
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(!!authInfo?.mfa_enabled)
  const cognitoAction = mfaEnabled ? 'deactivate_mfa' : 'activate_mfa'
  const onClickButton = () => setOpenModal(true)

  const onSubmit = async (mfaCode: string) => {
    // We set `sucessFlash` to false because this endpoint could return 401,
    // and this 401 is not considered an error by the axios interceptor, so while
    // the user is redirected is being redirected to the login page, we show a green
    // flash, which is inconsistent because the change was not saved.
    // That's why we show a manual flash when everything is ok.
    // REVIEW: Should we consider the 401 as an app error?
    const { success } = await asyncAction(
      () =>
        cognitoAPI.rpc(cognitoAction, {
          code: mfaCode,
        }),
      { successFlash: false }
    )

    if (success) {
      setOpenModal(false)
      setMfaEnabled(!mfaEnabled)
      app.setFlash({
        type: 'correct',
        message: mfaEnabled
          ? i18n.t('personal_settings.mfa.deactivated_successfully')
          : i18n.t('personal_settings.mfa.activated_successfully'),
      })
    }
  }

  const boxContent = () => {
    if (!mfaAvailable) {
      return <Text.Normal>{i18n.t('personal_settings.mfa.not_available')}</Text.Normal>
    }

    const buttonType: ButtonVariants = mfaEnabled ? 'negative' : 'primary'
    const buttonText = mfaEnabled
      ? i18n.t('personal_settings.mfa.deactivate_cta')
      : i18n.t('personal_settings.mfa.setup_cta')

    return (
      <>
        {renderStatusTag()}
        <Text.Normal>{i18n.t('personal_settings.mfa.setup_description')}</Text.Normal>

        <Button type={buttonType} onClick={onClickButton}>
          {buttonText}
        </Button>
      </>
    )
  }

  const renderStatusTag = () => {
    if (mfaEnabled) {
      return <Tag type="viridian">{i18n.t('personal_settings.mfa.active')}</Tag>
    } else {
      return <Tag type="grey">{i18n.t('personal_settings.mfa.not_configured')}</Tag>
    }
  }

  if (loadingAuthInfo) {
    return <Loading />
  }

  if (!canRenderMFA) {
    return null
  }

  return (
    <Section
      title={i18n.t('personal_settings.mfa.title')}
      description={i18n.t('personal_settings.mfa.description')}
      illustration="lock-user"
      link={{
        href: helpCenterUrl('mfa'),
        label: i18n.t('personal_settings.mfa.link'),
      }}
    >
      <Box
        alignItems="flexStart"
        borderRadius={{
          all: 'abs008',
        }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        gap="s12"
        padding="s24"
      >
        <Text.Normal size="300" weight="semibold">
          {i18n.t('personal_settings.mfa.setup_title')}
        </Text.Normal>
        {boxContent()}
      </Box>
      {openModal && (
        <MFASetupModal
          onClose={() => setOpenModal(false)}
          onSubmit={onSubmit}
          mfaEnabled={mfaEnabled}
        />
      )}
    </Section>
  )
}

export default observer(MFASettings)
